import React from "react";
import { Tooltip } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { CalendarClock } from "lucide-react";
import Icon from "../../../YourAIAgents/components/components/icon";
import ChannelLogo, { CHANNEL_PROVIDERS } from "./ChannelLogo";
import formatTime from "../../../../utils/formatTime";

const pillClass =
  "inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-2.5 py-1 text-xs font-medium text-foreground/80 hover:text-foreground hover:bg-muted transition-colors";

const UserMessage = ({
  message,
  msgIndex,
  onOpenPromptModal,
  onViewSchedule,
  automatedTask,
  onUploadKB,
}) => {
  return (
    <div className="flex flex-col items-end mb-5 group w-full">
      <div className="flex w-full items-start justify-end gap-3">
        <div className="flex flex-col items-end max-w-[60%]">
          <div className="bg-foreground/[0.04] text-foreground px-5 py-2.5 rounded-xl min-w-[120px]">
            <p className="text-[14px] font-normal m-0 break-words whitespace-pre-wrap">
              {message.content}
            </p>
          </div>

          <div className="flex items-center gap-2 mt-1.5">
            {automatedTask ? (
              <Tooltip title="Already automated, view schedule" placement="top">
                <button
                  type="button"
                  onClick={() => onViewSchedule?.(automatedTask.id)}
                  aria-label="View scheduled Task"
                  className={pillClass}
                >
                  <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{automatedTask.frequency}</span>
                  {automatedTask.clock && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <span>{automatedTask.clock}</span>
                    </>
                  )}
                  {automatedTask.channelName && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <ChannelLogo provider={automatedTask.provider} />
                      <span>{automatedTask.channelName}</span>
                    </>
                  )}
                </button>
              </Tooltip>
            ) : (
              <Tooltip title="Save and Automate Task" placement="top">
                <button
                  type="button"
                  onClick={() => onOpenPromptModal?.(message.content)}
                  aria-label="Automate in Slack and Teams"
                  className={pillClass}
                >
                  <span>Automate</span>
                  {CHANNEL_PROVIDERS.map((provider) => (
                    <ChannelLogo key={provider} provider={provider} />
                  ))}
                </button>
              </Tooltip>
            )}
          </div>
        </div>

        <span className="shrink-0 pt-3 text-muted-foreground text-[11px] font-semibold">
          {formatTime(message.time)}
        </span>
      </div>

      {message?.file_ids?.length > 0 && (
        <div className="flex flex-wrap gap-2 justify-end max-w-[60%] mt-2">
          {message.file_ids.slice(0, 3).map((item, index) => {
            const fileName = item?.name?.split("/").pop() || item?.name || "";
            const truncated =
              fileName.length > 10 ? `${fileName.slice(0, 10)}...` : fileName;
            const isAttachable = !(
              message?.file_type === "KB" ||
              fileName.includes(".png") ||
              fileName.includes(".jpg")
            );
            return (
              <div
                key={index}
                className="flex items-center gap-2 bg-card border border-border rounded-[6px] px-3 py-2 text-[12px] text-foreground"
              >
                <Icon id="file" />
                <span>{truncated}</span>
                {isAttachable && (
                  <span
                    className="cursor-pointer"
                    onClick={() => onUploadKB?.(item)}
                  >
                    <AddCircleOutlineIcon
                      sx={{ color: "hsl(var(--muted-foreground))", fontSize: "16px" }}
                    />
                  </span>
                )}
              </div>
            );
          })}
          {message.file_ids.length - 3 > 0 && (
            <span className="text-[12px] text-muted-foreground self-center">
              +{message.file_ids.length - 3} more
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default UserMessage;
